import { useId } from 'react';

type SearchBarProps = {
  value: string;
  onChange: (value: string) => void;
  /** Number of matches currently shown, announced to screen readers. */
  resultCount: number;
  className?: string;
  autoFocus?: boolean;
};

/**
 * The recipe search field. Filters by recipe name, tag, or ingredient as the
 * visitor types; results are announced politely for screen readers.
 */
export function SearchBar({
  value,
  onChange,
  resultCount,
  className = '',
  autoFocus,
}: SearchBarProps) {
  const inputId = useId();

  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-2 block font-display text-lg text-plume-900">
        Search the collection
      </label>
      <div className="relative">
        <svg
          viewBox="0 0 24 24"
          className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-plume-500"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M15.5 15.5 L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          id={inputId}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Try “gruyère”, “truffle”, or “campfire”…"
          autoComplete="off"
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- opt-in, used only on the dedicated search page */
          autoFocus={autoFocus}
          className="w-full rounded-full border-2 border-plume-200 bg-white py-3 pl-12 pr-5 text-plume-950 shadow-gloss placeholder:text-plume-600 focus:border-plume-500 focus:outline-none focus:ring-4 focus:ring-plume-200"
        />
      </div>
      <p aria-live="polite" className="mt-2 text-sm text-plume-700">
        {value.trim()
          ? `${resultCount} ${resultCount === 1 ? 'recipe matches' : 'recipes match'} “${value.trim()}”`
          : `${resultCount} recipes in the collection`}
      </p>
    </div>
  );
}
